import { CollapsibleSection, Colors, Heading, Text } from '@amelio/ui-library';
import { useState } from 'react';

import type { PendingSave } from '@/pages/ThematicMeetings/Plan/components/PendingSavesScope';
import type { ThematicMeeting } from '@/types/thematic-meetings';

import { EmployeeNoteList } from '@/components/EmployeeNotes/EmployeeNoteList';
import { useAmelioTranslations } from '@/hooks';
import { MEETINGS_TITLE } from '@/pages/ThematicMeetings/components/meetingStyles';
import { usePendingSave } from '@/pages/ThematicMeetings/Plan/components/PendingSavesScope';

/** The folder a note written in a meeting is filed under, the one the profile names "Rencontres". */
const MEETING_NOTE_CATEGORY = 'Collaboration';

const MEETING_NOTES_PAGE_SIZE = 5;

interface MeetingNotesSectionProps {
    meeting: ThematicMeeting;
    /** A completed meeting: its notes are read, not written. */
    disabled: boolean;
    dataTestId?: string;
}

/**
 * Every note written in this meeting, whatever its folder, closed until the manager asks for it.
 * The list only mounts once the section is open, so a closed section costs no request.
 */
export const MeetingNotesSection = ({
    meeting,
    disabled,
    dataTestId = 'thematic-meeting-notes',
}: MeetingNotesSectionProps) => {
    const { t } = useAmelioTranslations();
    const [isOpen, setIsOpen] = useState(false);
    // Closing the section unmounts the list, and the note being written goes with it.
    const [unsavedNote, setUnsavedNote] = useState<{ save: PendingSave } | null>(null);
    usePendingSave(disabled ? null : (unsavedNote?.save ?? null));

    return (
        <CollapsibleSection
            isOpen={isOpen}
            onToggle={() => {
                setIsOpen((current) => !current);
                setUnsavedNote(null);
            }}
            variant={'glass'}
            dataTestId={dataTestId}
            header={
                <div className={'flex items-center justify-between gap-4'}>
                    <Heading
                        level={3}
                        color={Colors.NAVY}
                        className={MEETINGS_TITLE}
                    >
                        {t('thematicMeetings.plan.meetingNotesTitle')}
                    </Heading>

                    <Text
                        size={'small'}
                        color={Colors.NAVY}
                    >
                        {isOpen
                            ? t('thematicMeetings.plan.meetingNotesClose')
                            : t('thematicMeetings.plan.meetingNotesOpen')}
                    </Text>
                </div>
            }
        >
            <div className={'px-10 pt-6 pb-8'}>
                <EmployeeNoteList
                    employeeId={meeting.employeeId}
                    category={null}
                    origin={{ thematicMeetingId: meeting.id }}
                    newNoteCategory={MEETING_NOTE_CATEGORY}
                    pageSize={MEETING_NOTES_PAGE_SIZE}
                    readOnly={disabled}
                    onUnsavedNoteChange={(save) => setUnsavedNote(save && { save })}
                    labels={{
                        empty: t('thematicMeetings.plan.meetingNotesEmpty'),
                        placeholder: t('thematicMeetings.plan.meetingNotesPlaceholder'),
                        deleteTitle: t('thematicMeetings.plan.meetingNotesDeleteTitle'),
                        deleteMessage: t('thematicMeetings.plan.meetingNotesDeleteMessage'),
                        loadError: t('thematicMeetings.plan.meetingNotesLoadError'),
                        forbidden: t('thematicMeetings.plan.meetingNotesForbidden'),
                        createError: t('thematicMeetings.plan.meetingNotesCreateError'),
                        updateError: t('thematicMeetings.plan.meetingNotesUpdateError'),
                        deleteError: t('thematicMeetings.plan.meetingNotesDeleteError'),
                        pageSummary: (range) =>
                            t('thematicMeetings.plan.meetingNotesPageSummary', range),
                    }}
                    dataTestId={`${dataTestId}-list`}
                />
            </div>
        </CollapsibleSection>
    );
};
